import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { ClipboardCheck, Loader2, CheckCircle2, XCircle, Search, ArrowRight, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "../components/ui/dialog";

export default function MissingDocsPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const checkFilter = searchParams.get("check") || "";

    useEffect(() => {
        api.get("/documents/required-docs/report")
            .then((r) => setData(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const rows = useMemo(() => {
        if (!data) return [];
        let list = data.employees;
        if (checkFilter) list = list.filter((e) => e.missing.includes(checkFilter));
        const q = search.trim().toLowerCase();
        if (q) {
            list = list.filter((e) =>
                `${e.cognome} ${e.nome}`.toLowerCase().includes(q) || (e.matricola || "").toLowerCase().includes(q));
        }
        return list;
    }, [data, checkFilter, search]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 className="h-6 w-6 animate-spin text-slate-300" />
            </div>
        );
    }
    if (!data) return null;

    const filterLabel = checkFilter ? data.checks.find((c) => c.key === checkFilter)?.label : null;

    const requestDocs = async () => {
        setSending(true);
        try {
            const { data: res } = await api.post("/documents/required-docs/request-missing", {
                employee_ids: rows.map((e) => e.id),
                check_keys: checkFilter ? [checkFilter] : null,
            });
            if (!res.emails_enabled) {
                toast.warning(`Le email sono disattivate: nessun invio effettuato (${res.targets} destinatari pronti). Riattivale e riprova.`);
            } else {
                toast.success(
                    `Richiesta avviata: ${res.queued} email in coda` +
                    (res.no_email ? ` · ${res.no_email} senza indirizzo email` : "") +
                    " — dettagli nel Registro Email",
                );
            }
            setConfirmOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore invio richieste");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="mx-auto max-w-6xl px-6 py-8" data-testid="missing-docs-page">
            <div className="mb-6 flex items-start gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                    <ClipboardCheck className="h-6 w-6" />
                </div>
                <div>
                    <h1 className="font-display text-2xl font-bold text-slate-800">Documenti obbligatori mancanti</h1>
                    <p className="text-sm text-slate-500">
                        Fascicolo completo: <b className="text-emerald-600">{data.complete}</b> su {data.total_active} dipendenti attivi ·{" "}
                        <b className="text-amber-700">{data.employees.length}</b> con documenti mancanti
                    </p>
                </div>
            </div>

            <div className="mb-4 flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    onClick={() => setSearchParams({})}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${!checkFilter ? "bg-[#376DB3] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                    data-testid="filter-all"
                >
                    Tutti ({data.employees.length})
                </button>
                {data.checks.map((c) => (
                    <button
                        key={c.key}
                        type="button"
                        onClick={() => setSearchParams(checkFilter === c.key ? {} : { check: c.key })}
                        className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${checkFilter === c.key ? "bg-[#376DB3] text-white" : (data.totals[c.key] || 0) > 0 ? "bg-amber-50 text-amber-800 hover:bg-amber-100" : "bg-emerald-50 text-emerald-700"}`}
                        data-testid={`filter-${c.key}`}
                    >
                        {c.label}: {data.totals[c.key] || 0}
                    </button>
                ))}
                <div className="relative ml-auto">
                    <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Cerca dipendente o matricola…"
                        className="h-9 w-64 rounded-md border border-slate-200 bg-white pl-8 pr-3 text-sm text-slate-700"
                        data-testid="missing-docs-search"
                    />
                </div>
                <Button
                    onClick={() => setConfirmOpen(true)}
                    disabled={rows.length === 0}
                    className="h-9 gap-2 bg-[#376DB3] text-white hover:bg-[#2c5a96]"
                    data-testid="request-missing-docs-btn"
                >
                    <Mail className="h-4 w-4" />
                    Richiedi via email ({rows.length})
                </Button>
            </div>

            {rows.length === 0 ? (
                <div className="rounded-lg border border-dashed border-emerald-200 bg-emerald-50/50 p-10 text-center text-sm text-emerald-700" data-testid="missing-docs-empty">
                    <CheckCircle2 className="mx-auto mb-2 h-8 w-8" />
                    Nessun dipendente con documenti mancanti{checkFilter || search ? " per il filtro selezionato" : ""}.
                </div>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                <th className="px-3 py-2.5">Matr.</th>
                                <th className="px-3 py-2.5">Dipendente</th>
                                {data.checks.map((c) => (
                                    <th key={c.key} className="px-2 py-2.5 text-center" title={c.label}>
                                        {c.label.split(" ")[0]}
                                    </th>
                                ))}
                                <th className="px-3 py-2.5 text-right">Mancanti</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((e) => (
                                <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`missing-row-${e.matricola}`}>
                                    <td className="px-3 py-2 font-mono-numbers text-xs text-slate-500">{e.matricola}</td>
                                    <td className="px-3 py-2">
                                        <Link to={`/dipendenti/${e.id}`} className="flex items-center gap-1 font-semibold text-slate-700 hover:text-[#376DB3] hover:underline">
                                            {e.cognome} {e.nome}
                                            <ArrowRight className="h-3 w-3 text-slate-300" />
                                        </Link>
                                    </td>
                                    {data.checks.map((c) => (
                                        <td key={c.key} className="px-2 py-2 text-center">
                                            {e.missing.includes(c.key)
                                                ? <XCircle className="mx-auto h-4 w-4 text-red-500" data-testid={`missing-${e.matricola}-${c.key}`} />
                                                : <CheckCircle2 className="mx-auto h-4 w-4 text-emerald-400" />}
                                        </td>
                                    ))}
                                    <td className="px-3 py-2 text-right">
                                        <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-bold text-red-700">{e.missing.length}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <p className="mt-3 text-xs text-slate-400">
                Carta d'identità e Tessera sanitaria vengono riconosciute dal titolo dei documenti in categoria «Documento identità».
                Per la Formazione vale qualsiasi certificato, esclusi i corsi specialistici (primo soccorso, antincendio, preposto, rischio chimico, PLE).
                Se un documento c'è ma non viene riconosciuto, correggi il titolo dalla scheda dipendente (matita).
            </p>

            <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <DialogContent className="max-w-md" data-testid="request-missing-docs-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-slate-800">Richiedi documenti via email</DialogTitle>
                        <DialogDescription>
                            Verrà inviata una email a ciascuno dei <b>{rows.length}</b> dipendenti in elenco con la lista dei
                            {filterLabel ? <> documenti mancanti (solo <b>{filterLabel}</b>)</> : " loro documenti mancanti"},
                            invitandoli a consegnarli in ufficio o caricarli dal portale.
                        </DialogDescription>
                    </DialogHeader>
                    <p className="text-xs text-slate-500">
                        I dipendenti senza indirizzo email verranno saltati. Gli invii vengono registrati nel Registro Email.
                    </p>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={() => setConfirmOpen(false)}>Annulla</Button>
                        <Button
                            onClick={requestDocs}
                            disabled={sending}
                            className="gap-2 bg-[#376DB3] text-white hover:bg-[#2c5a96]"
                            data-testid="request-missing-docs-confirm"
                        >
                            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                            Invia richieste
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
